import type { ScenarioModel, ScenarioRunSetModel } from '@/shared/generated'

import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
	every,
	filter,
	find,
	flatten,
	fromPairs,
	groupBy,
	isEmpty,
	keys,
	map,
	mapValues,
	negate,
	pipe,
	uniq,
	values,
} from 'lodash/fp'
import { BoxIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useRunScenarioSetsMutation } from '@/entities/scenario'

import { client } from '@/shared/api/client'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Field, FieldContent, FieldLabel } from '@/shared/components/ui/field'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import {
	MultiSelect,
	MultiSelectContent,
	MultiSelectItem,
	MultiSelectList,
	MultiSelectSearch,
	MultiSelectTrigger,
	MultiSelectValue,
} from '@/shared/components/ui/multi-select'
import { Separator } from '@/shared/components/ui/separator'
import { ampli } from '@/shared/lib/ampli'
import { queryClient } from '@/shared/lib/query-client'
import { uuid } from '@/shared/tools/utils'

import { useOptimizationGroupsOptions } from '../lib/useOptimizationGroupsOptions'
import { OptimizationGroupScenariosSelect } from './OptimizationGroupScenariosSelect'

const preparePreselectedValues = pipe(
	groupBy('optimization_group_id'),
	mapValues(map('id'))
)
export function ScenariosCompareModal({
	isOpen,
	close,
	scenarios,
}: {
	isOpen: boolean
	close: () => void
	scenarios: ScenarioModel[]
}) {
	const { t } = useTranslation()

	const navigate = useNavigate()
	const [selected, setSelected] = useState<Record<string, string[]>>(() =>
		preparePreselectedValues(scenarios)
	)
	const selectedOptimizationGroupIds = useMemo(() => keys(selected), [selected])
	const { optimizationGroupsOptions, isLoading } = useOptimizationGroupsOptions(
		{ enabled: isOpen }
	)
	const runScenarioSetsMutation = useRunScenarioSetsMutation({
		onSuccess: ({ id }: ScenarioRunSetModel) => {
			navigate({ to: `/p/whatif/runs/${id}/summary` })
			queryClient.invalidateQueries({
				queryKey: ['scenarios-run-sets'],
			})
			close()
		},
	})
	const canBeProcessed = useMemo(
		() => !isEmpty(selected) && pipe(values, every(negate(isEmpty)))(selected),
		[selected]
	)
	const handleOptimizationGroupsToggle = useCallback((ids: string[]) => {
		setSelected((prev) => fromPairs(map((id) => [id, prev[id] ?? []], ids)))
	}, [])
	const handleScenariosToggle = useCallback((ids: string[], ogId: string) => {
		setSelected((prev) => ({ ...prev, [ogId]: ids }))
	}, [])
	const handleRunScenarios = useCallback(async () => {
		const selectedScenarioIds = pipe(values, flatten, uniq)(selected)
		ampli.whatIfSummaryRun()
		const toastId = toast.loading(
			t('general_processing', { defaultValue: 'Processing...' })
		)

		if (!selectedScenarioIds.length) return
		const queryParams = {
			optimization_group_ids: selectedOptimizationGroupIds,
		}

		const [mainGlobalPcSet, scenarios] = await Promise.all([
			queryClient.fetchQuery({
				queryKey: ['main-global-pc-set'],
				queryFn: () =>
					client.globalPcSet
						.globalPcSetList()
						.then(find(({ is_main }) => Boolean(is_main))),
			}),
			queryClient.fetchQuery({
				queryKey: ['scenarios-query-list', queryParams, selectedScenarioIds],
				queryFn: () =>
					client.scenario
						.scenariosQueryList(queryParams)
						.then(filter(({ id }) => selectedScenarioIds.includes(id))),
			}),
		])
		toast.dismiss(toastId)

		if (!scenarios?.length || !mainGlobalPcSet) return

		const combination_id = uuid()
		const requestBody = {
			scenario_runs: scenarios?.map(
				({ name, id, settings, optimization_group_id }) => ({
					name,
					combination_id,
					global_pc_set_id: mainGlobalPcSet.id,
					optimization_group_id,
					scenario_id: id,
					settings: settings || {},
				})
			),
		}
		toast.promise(runScenarioSetsMutation.mutateAsync(requestBody), {
			loading: t('general_processing'),
			error: t('whatif.compare.global_sets.run.error', {
				defaultValue: 'Error while running global sets',
			}),
		})
	}, [selected, selectedOptimizationGroupIds, runScenarioSetsMutation])

	return (
		<Modal open={isOpen} onOpenChange={(open) => !open && close()}>
			<ModalContent className='max-w-4xl overflow-hidden'>
				<div className='relative flex h-[90vh] w-full flex-col gap-4'>
					<ModalHeader>
						<ModalTitle>
							{t('whatif.compare.scenarios.title', {
								defaultValue: 'Compare scenarios',
							})}
						</ModalTitle>
					</ModalHeader>
					<ErrorBoundary>
						<Field className='grid w-full'>
							<FieldLabel>
								{t('whatif.select_optimization_groups.label', {
									defaultValue: 'Select Optimization groups to compare',
								})}
							</FieldLabel>
							<FieldContent className='grid'>
								<MultiSelect
									isLoading={isLoading}
									options={optimizationGroupsOptions}
									value={selectedOptimizationGroupIds}
									onValueChange={handleOptimizationGroupsToggle}
								>
									<MultiSelectTrigger className='w-full overflow-hidden'>
										<MultiSelectValue
											maxDisplay={7}
											placeholder={t('general_select', {
												defaultValue: 'Select',
											})}
										/>
									</MultiSelectTrigger>
									<MultiSelectContent>
										<MultiSelectSearch
											placeholder={t('general_search', {
												defaultValue: 'Search...',
											})}
										/>
										<MultiSelectList>
											{optimizationGroupsOptions?.map((option) => (
												<MultiSelectItem
													key={option.value}
													value={option.value}
												>
													{option.label}
												</MultiSelectItem>
											))}
										</MultiSelectList>
									</MultiSelectContent>
								</MultiSelect>
							</FieldContent>
						</Field>

						<ModalBody className='overflow-x-hidden overflow-y-auto border-y bg-card py-4'>
							<div className='ml-4 box-border flex w-full flex-col gap-2 border-l px-4'>
								<div className='flex w-full flex-col gap-2 pb-10'>
									<div className='flex items-center gap-2'>
										<div className='-ml-8 flex size-7 shrink-0 items-center justify-center rounded-full border bg-muted'>
											<BoxIcon className='size-4 text-muted-foreground' />
										</div>
										<div className='flex w-full items-center gap-2 overflow-hidden'>
											<Badge>
												{t('combination', { defaultValue: 'Combination' })} #1
											</Badge>
											<Separator className='shrink' />
										</div>
									</div>
									{selectedOptimizationGroupIds?.map((optimizationGroupId) => (
										<OptimizationGroupScenariosSelect
											key={optimizationGroupId}
											optimizationGroupId={optimizationGroupId}
											values={selected[optimizationGroupId]}
											onValueChange={handleScenariosToggle}
										/>
									))}
								</div>
							</div>
						</ModalBody>
					</ErrorBoundary>
					<ModalFooter className='sm:justify-between'>
						<ModalClose asChild>
							<Button onClick={close}>{t('general_cancel')}</Button>
						</ModalClose>
						<Button
							onClick={handleRunScenarios}
							variant='primary'
							disabled={!canBeProcessed}
						>
							{t('scenario.run.confirm.ok')}
						</Button>
					</ModalFooter>
				</div>
			</ModalContent>
		</Modal>
	)
}
